import FormSwitch from '@/components/FormComponents/FormSwitch';
import ShadCnFormInput from '@/components/FormComponents/ShadCnFormInput';
import Spinner from '@/components/Spinner';
import { Button } from '@/components/ui/button';
import { SubmitButton, CancelButton } from '@/components/ui/buttons';
import { TRAINING_CONTENT_TYPE } from '@/constants/bot';
import { useGetEntries, useUpsertEntry } from '@/hooks/useBot';
import { useClickOutside } from '@/hooks/useClickOutside';
import { searchPageLink } from '@/services/offers';
import { ICreateEntryBody } from '@/types';
import { useAnalytics } from '@/hooks/useAnalytics';
import { CHATBOT_TRAINING_UPDATED } from '@/lib/analytics-events';
import { handleShowError } from '@/utils';
import { truncateText } from '@/utils/text';
import { createEntrySchema } from '@/validations/bot';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { debounce } from 'lodash';
import { PlusCircle, Trash2, BookOpen } from 'lucide-react';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { FormProvider, Resolver, useFieldArray, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import SectionCard from './shared/SectionCard';

interface TrainingDataProps {
  token: string;
  isBackButton?: boolean;
}

interface ITrainingForm {
  training_data: {
    trigger_phrase: string;
    content_value: string;
    is_url: boolean;
  }[];
}

const TrainingData: FC<TrainingDataProps> = ({ token }) => {
  const { capture } = useAnalytics();
  const [urlSearchValue, setUrlSearchValue] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [activeSearchIndex, setActiveSearchIndex] = useState<number | null>(null);
  const searchBoxRef = useRef<HTMLDivElement | null>(null);

  const form = useForm<ITrainingForm>({
    defaultValues: {
      training_data: [
        {
          trigger_phrase: '',
          content_value: '',
          is_url: false,
        },
      ],
    },
    resolver: yupResolver(createEntrySchema) as Resolver<ITrainingForm>,
  });

  const { control, handleSubmit, setValue, watch } = form;
  const { fields, append, remove, update } = useFieldArray({
    control,
    name: 'training_data',
  });
  const controlledFields = (fields || []).map((field, index) => ({
    ...field,
    ...watch(`training_data`)?.[index],
  }));

  const { mutate: upsertEntryMutate, isPending: isUpsertingEntry } = useUpsertEntry();
  const { data: entries } = useGetEntries(token);
  // URL search functionality
  const {
    mutate: searchPageLinkMutate,
    data: searchResults,
    isPending: isSearching,
  } = useMutation({
    mutationFn: (query: string) => searchPageLink(query),
  });

  useClickOutside(searchBoxRef, () => {
    setShowSearchResults(false);
  });

  useEffect(() => {
    if (entries?.data?.length) {
      form.reset({
        training_data: entries.data.map((item) => ({
          trigger_phrase: item.trigger_phrase,
          content_value: item.content_value,
          is_url: item.content_type === TRAINING_CONTENT_TYPE.URL,
        })),
      });
    }
  }, [entries?.data]);

  const handleSearchPageLink = useCallback(
    debounce((query: string) => {
      if (query.trim().length > 0) {
        searchPageLinkMutate(query, {
          onError: handleShowError,
        });
      }
    }, 500),
    [searchPageLinkMutate],
  );

  const handleSelectPageLink = (url: string, index: number) => {
    const triggerPhrase = controlledFields[index].trigger_phrase;
    update(index, {
      trigger_phrase: triggerPhrase,
      content_value: url,
      is_url: true,
    });
    setUrlSearchValue('');
    setShowSearchResults(false);
    setActiveSearchIndex(null);
  };

  const onSubmit = (data: ITrainingForm) => {
    const body: ICreateEntryBody = {
      chat_token: token,
      training_data: data.training_data.map((item) => ({
        trigger_phrase: item.trigger_phrase,
        content_type: item.is_url ? TRAINING_CONTENT_TYPE.URL : TRAINING_CONTENT_TYPE.TEXT,
        content_value: item.content_value,
        is_active: true,
      })),
    };

    upsertEntryMutate(body, {
      onSuccess: () => {
        toast.success('Training data saved successfully');
        capture(CHATBOT_TRAINING_UPDATED, {
          entry_count: data.training_data.length,
        });
      },
      onError: handleShowError,
    });
  };

  const addNewTrainingItem = () => {
    append({
      trigger_phrase: '',
      content_value: '',
      is_url: false,
    });
  };

  const toggleMode = (index: number, isUrl: boolean) => {
    // Clear the content when switching modes
    setValue(`training_data.${index}.content_value`, '');
    setValue(`training_data.${index}.is_url`, isUrl, { shouldValidate: true });
    setUrlSearchValue('');
    setShowSearchResults(false);
    setActiveSearchIndex(null);
  };

  const handleUrlSearchChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const value = e.target.value;
    setValue(`training_data.${index}.content_value`, value, { shouldValidate: true });
    setUrlSearchValue(value);
    setActiveSearchIndex(index);
    handleSearchPageLink(value);

    if (value.trim().length > 0) {
      setShowSearchResults(true);
    } else {
      setShowSearchResults(false);
    }
  };

  const handleRemoveItem = (index: number) => {
    if (fields.length > 1) {
      remove(index);
    } else {
      toast.error('At least one training item is required');
    }
  };

  return (
    <SectionCard
      icon={BookOpen}
      title="Q&A Pairs"
      subtitle="Add custom triggers and responses for your chatbot."
    >
          <FormProvider {...form}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div>
                <div className='grid grid-cols-12 gap-4 mb-6 font-medium text-gray-700'>
                  <div className='col-span-4'>
                    <h4 className='text-sm'>If user asks about:</h4>
                  </div>
                  <div className='col-span-6'>
                    <h4 className='text-sm'>Content Source:</h4>
                  </div>
                  <div className='col-span-1'>
                    <h4 className='text-sm'>Mode</h4>
                  </div>
                  <div className='col-span-1' />
                </div>

                {controlledFields.map((field, index) => (
                  <div key={field.id} className='grid grid-cols-12 gap-4 mb-6'>
                    <div className='col-span-4'>
                      <ShadCnFormInput
                        name={`training_data.${index}.trigger_phrase`}
                        placeholder='Enter trigger phrase'
                        aria-label={`Trigger phrase for item ${index + 1}`}
                        required
                      />
                    </div>

                    <div className='col-span-6'>
                      {!field.is_url ? (
                        <ShadCnFormInput
                          name={`training_data.${index}.content_value`}
                          placeholder='Enter text response'
                          aria-label={`Text response for item ${index + 1}`}
                          required
                        />
                      ) : (
                        <div
                          ref={index === activeSearchIndex ? searchBoxRef : null}
                          className='border rounded-md max-h-20 p-2.5 flex flex-wrap gap-3 relative'
                        >
                          <input
                            type='text'
                            placeholder='eg: www.abc.com'
                            className='border-none outline-none text-sm font-medium flex-1'
                            value={
                              index === activeSearchIndex ? urlSearchValue : field.content_value
                            }
                            onChange={(e) => handleUrlSearchChange(e, index)}
                            onFocus={() => {
                              setActiveSearchIndex(index);
                              setUrlSearchValue(field.content_value);
                              if (field.content_value) {
                                setShowSearchResults(true);
                                handleSearchPageLink(field.content_value);
                              }
                            }}
                          />

                          {showSearchResults && activeSearchIndex === index && (
                            <div className='absolute left-0 top-full w-full max-h-[200px] overflow-auto shadow-drop bg-white mt-2 z-10 border rounded-md'>
                              {isSearching && (
                                <div className='w-full h-full flex justify-center items-center py-4'>
                                  <Spinner />
                                </div>
                              )}
                              <ul role='listbox' aria-label='URL search results'>
                                {(searchResults?.data || [])?.map((item: any) => (
                                  <li
                                    className='break-all text-sm text-gray-900 font-medium hover:bg-[#F4F5F5] p-3 cursor-pointer'
                                    key={item?.link_id}
                                    onMouseDown={(e) => {
                                      e.stopPropagation();
                                      handleSelectPageLink(item?.link, index);
                                    }}
                                    role='option'
                                  >
                                    {truncateText(item?.link, 100)}
                                  </li>
                                ))}
                                {searchResults?.data && searchResults.data.length === 0 && (
                                  <li className='text-sm text-gray-500 font-medium p-3'>
                                    No results found. Type a valid URL.
                                  </li>
                                )}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <div className='col-span-1 toggle-button flex items-center gap-2'>
                      <FormSwitch
                        name={`training_data.${index}.is_url`}
                        onCheckedChange={(checked) => {
                          toggleMode(index, checked);
                        }}
                      />
                      <p className='text-sm'>{field.is_url ? 'URL' : 'Text'}</p>
                    </div>

                    <div className='col-span-1'>
                      <Button type='button' variant='ghost' onClick={() => handleRemoveItem(index)}>
                        <Trash2 size={20} color='red' />
                      </Button>
                    </div>
                  </div>
                ))}

                <div className='mt-4 mb-3'>
                  <button
                    type='button'
                    className='flex items-center justify-center w-full py-3 border border-dashed border-gray-300 rounded-lg text-blue-600 font-medium hover:bg-blue-50 transition-all duration-200 text-sm'
                    onClick={addNewTrainingItem}
                    aria-label='Add new training item'
                  >
                    <PlusCircle className='mr-2' size={20} /> Add New Training Item
                  </button>
                </div>
              </div>

              <div className='flex justify-end items-center pt-4 border-t border-border gap-2'>
                <CancelButton aria-label='Cancel changes' disabled={isUpsertingEntry}>
                  Cancel
                </CancelButton>
                <SubmitButton aria-label='Save changes' isLoading={isUpsertingEntry}>
                  Save Changes
                </SubmitButton>
              </div>
            </form>
          </FormProvider>
    </SectionCard>
  );
};

export default TrainingData;
